import React, { ComponentType, FC, createContext } from 'react';
import { Subtract } from 'utility-types';

import { Network, SocialAccount, useGetUserSocialConnectionsQuery } from 'api';
import { Loaders, QueryManager } from 'components';
import { useCurrentUser } from 'hooks';

const SOCIAL_MEDIA_CONNECTIONS = [Network.Linkedin, Network.Twitter];
const THIRD_PARTY_INTEGRATIONS = [Network.Msteams];

export interface IWithSocialConnections {
  connections: SocialAccount[];
  integrations: SocialAccount[];
}

export const SocialConnectionsContext = createContext<IWithSocialConnections>({
  connections: [],
  integrations: [],
});

export const SocialConnectionsProvider: FC = ({ children }) => {
  const currentUser = useCurrentUser();
  const queryResult = useGetUserSocialConnectionsQuery({
    variables: { user_id: currentUser.id },
  });

  function getSocialConnections(connections: SocialAccount[]) {
    return connections.filter(connection => {
      return SOCIAL_MEDIA_CONNECTIONS.includes(connection.provider);
    });
  }

  function getIntegrations(connections: SocialAccount[]) {
    return connections.filter(connection => {
      return THIRD_PARTY_INTEGRATIONS.includes(connection.provider);
    });
  }

  return (
    <QueryManager loader={<Loaders.StandardNoHeader />} {...queryResult}>
      {({ data }) => {
        const allConnections = data?.getUserSocialConnections?.items ?? [];
        const connections = getSocialConnections(allConnections);
        const integrations = getIntegrations(allConnections);

        return (
          <SocialConnectionsContext.Provider value={{ connections, integrations }}>
            {children}
          </SocialConnectionsContext.Provider>
        );
      }}
    </QueryManager>
  );
};

export const withSocialConnections = <T extends IWithSocialConnections>(
  Component: ComponentType<T>,
) => (props: Subtract<T, IWithSocialConnections>) => (
  <SocialConnectionsContext.Consumer>
    {(providerProps: IWithSocialConnections) => <Component {...(props as T)} {...providerProps} />}
  </SocialConnectionsContext.Consumer>
);
